import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import LoadingOverlay from '../common/LoadingOverlay';
import { useAuth } from '../../context/AuthContext';
import { AppProvider } from '@toolpad/core/AppProvider';
import { appTheme } from '../../theme/appTheme';
import DashboardNavigationBar from '../DashboardNavigationBar/DashboardNavigationBar';
import { stripeService } from '../../services/api/stripeService';
import {
  DashboardContainer,
  SidebarContainer,
  ContentWrapper,
} from '../BusinessDashboard/BusinessDashboard.styled';
import {
  PageWrapper,
  BackLink,
  HeaderRow,
  PageHeading,
  SubHeading,
  InvoiceList,
  InvoiceRow,
  InvoicePrimary,
  PeriodLabel,
  DescriptionLabel,
  RowActions,
  Amount,
  StatusBadge,
  ActionLink,
  EmptyState,
} from './PaymentInvoices.styled';

/* ════════════════════════════════════════════════════════
   Normalize a raw Stripe invoice into the flat shape the UI renders.
   The API returns raw Stripe objects: Unix timestamps, snake_case keys,
   amount in `total`, and the real service period on the first line item.
   ════════════════════════════════════════════════════════ */
const toIso = (unixSeconds) =>
  typeof unixSeconds === 'number' ? new Date(unixSeconds * 1000).toISOString() : null;

const normalizeInvoice = (inv) => {
  const line = inv.lines?.data?.[0];
  return {
    id: inv.id,
    number: inv.number ?? null,
    status: inv.status ?? null,
    // top-level description is usually null on subscription invoices —
    // fall back to the line-item description, then the invoice number
    description: inv.description || line?.description || inv.number || 'Invoice',
    amount: inv.total ?? inv.amount_due ?? 0,
    currency: inv.currency || 'usd',
    created: toIso(inv.created),
    // prefer the line-item period (actual service window) over the
    // top-level period, which equals `created` on subscription_create invoices
    periodStart: toIso(line?.period?.start ?? inv.period_start ?? inv.created),
    periodEnd: toIso(line?.period?.end ?? inv.period_end),
    hostedInvoiceUrl: inv.hosted_invoice_url ?? null,
    invoicePdf: inv.invoice_pdf ?? null,
  };
};

/* ─── Formatters ─── */
const formatCurrency = (amountInCents, currency = 'usd') =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: (currency || 'usd').toUpperCase(),
  }).format((amountInCents || 0) / 100);

const formatMonth = (iso) =>
  iso ? new Date(iso).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : '—';

const formatFullDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '—';

const STATUS_LABELS = {
  paid: 'Paid',
  open: 'Open',
  draft: 'Draft',
  void: 'Void',
  uncollectible: 'Uncollectible',
};

/* ════════════════════════════════════════════════════════
   Component
   ════════════════════════════════════════════════════════ */
const PaymentInvoices = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [invoices, setInvoices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  /* ─── Auth guard (mirrors AddPaymentMethod) ─── */
  useEffect(() => {
    if (!user) return;
    if (user?.Role !== 'business_user' && user?.Role !== 'admin_user') {
      navigate('/');
    }
  }, [user, navigate]);

  /* ─── Load invoices from the backend ─── */
  useEffect(() => {
    const loadInvoices = async () => {
      setIsLoading(true);
      setError('');
      try {
        const data = await stripeService.getInvoices();
        const normalized = (data.invoices || []).map(normalizeInvoice);
        setInvoices(normalized);
      } catch (err) {
        setError(err.message || 'Could not load your invoices. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };
    loadInvoices();
  }, []);

  return (
    <AppProvider branding={{ title: 'Invoices' }} theme={appTheme}>
      <DashboardContainer>
        <SidebarContainer>
          <DashboardNavigationBar />
        </SidebarContainer>

        <ContentWrapper>
          <PageWrapper>
            <BackLink onClick={() => navigate('/payment-methods')}>
              <ArrowBackIcon style={{ fontSize: '1rem' }} />
              Back to Payment Methods
            </BackLink>

            <HeaderRow>
              <div>
                <PageHeading>Invoices</PageHeading>
                <SubHeading>View and download your billing history.</SubHeading>
              </div>
            </HeaderRow>

            {error && (
              <EmptyState>
                <p>{error}</p>
              </EmptyState>
            )}

            {!error && !isLoading && invoices.length === 0 && (
              <EmptyState>
                <ReceiptLongIcon style={{ fontSize: '2rem', opacity: 0.4 }} />
                <p>No invoices yet. Your billing history will appear here.</p>
              </EmptyState>
            )}

            {!error && invoices.length > 0 && (
              <InvoiceList>
                {invoices.map((inv) => (
                  <InvoiceRow key={inv.id}>
                    <InvoicePrimary>
                      <PeriodLabel>{formatMonth(inv.periodStart)}</PeriodLabel>
                      <DescriptionLabel>
                        {inv.description} · Issued {formatFullDate(inv.created)}
                      </DescriptionLabel>
                    </InvoicePrimary>

                    <RowActions>
                      <Amount>{formatCurrency(inv.amount, inv.currency)}</Amount>

                      <StatusBadge $status={inv.status}>
                        {STATUS_LABELS[inv.status] || inv.status}
                      </StatusBadge>

                      {inv.invoicePdf && (
                        <ActionLink
                          href={inv.invoicePdf}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <PictureAsPdfIcon style={{ fontSize: '1rem' }} />
                          PDF
                        </ActionLink>
                      )}
                    </RowActions>
                  </InvoiceRow>
                ))}
              </InvoiceList>
            )}
          </PageWrapper>
        </ContentWrapper>
      </DashboardContainer>

      {isLoading && <LoadingOverlay />}
    </AppProvider>
  );
};

export default PaymentInvoices;